import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface CuCO3PowderProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Total mass (in grams) of powder in the test tube — drives pile size. */
  massG?: number;
  /** Maximum mass the pile is sized against. */
  capacityG?: number;
  /** 0 = fresh green CuCO₃, 1 = fully decomposed black CuO. */
  heatingProgress?: number;
  /** Inner radius of the container the pile sits inside. */
  containerRadius?: number;
}

/**
 * Copper(II) carbonate pile — a small mound of green CuCO₃ powder that
 * progressively turns black (CuO) as it is heated. Mirrors the existing
 * `CuOPowder` colour-lerp pattern (CuO experiment) but with different
 * colour endpoints. Used inside the heated test tube during Part A of
 * Activity 3.18.
 */
export function CuCO3Powder({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  massG = 0,
  capacityG = 1.5,
  heatingProgress = 0,
  containerRadius = 0.05,
}: CuCO3PowderProps) {
  const matRef = useRef<THREE.MeshStandardMaterial>(null);

  useFrame(() => {
    if (matRef.current) {
      const t = Math.max(0, Math.min(1, heatingProgress));
      // Lerp from fresh CuCO₃ green to fully-decomposed CuO black
      const r = 0.25 - 0.15 * t;
      const g = 0.62 - 0.55 * t;
      const b = 0.35 - 0.25 * t;
      matRef.current.color.setRGB(r, g, b);
      matRef.current.roughness = 0.95;
      matRef.current.metalness = 0.02;
    }
  });

  const fraction = Math.max(0, Math.min(1, massG / Math.max(0.0001, capacityG)));
  const pileRadius = Math.min(containerRadius * 0.85, 0.015 + 0.022 * fraction);
  const pileHeight = 0.012 + 0.024 * fraction;

  if (massG <= 0.0001) return null;

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Main mound — unit cone scaled to a small heap. Centred on the
          test tube floor (caller positions the group so the base of the
          mound rests on the curved bottom). */}
      <mesh
        position={[0, pileHeight / 2, 0]}
        scale={[pileRadius, pileHeight / 2, pileRadius]}
        castShadow
      >
        <coneGeometry args={[1, 2, 22]} />
        <meshStandardMaterial
          ref={matRef}
          color="#3fa563"
          roughness={0.95}
          metalness={0.02}
        />
      </mesh>
    </group>
  );
}

export default CuCO3Powder;
